import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, TextField } from '@mui/material';

import type { FilterValue } from 'src/components/app/context/FilterContext';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import { FILTER_KEYS } from 'src/components/followUp/hooks/constants';
import type {
  DateRangeAsStrings,
  RequiredDateRange,
} from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';
import {
  dateRangeToFilter,
  filterToDateRange,
} from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';

import DateFilterModal from './DateFilterModal';
import { calculateInitialDateRange, dateRangeToReadableString } from './helper';

interface DateFilterControlProps {
  comparison?: boolean;
  minDate?: Date;
}

const DateFilterControl: FC<DateFilterControlProps> = ({
  comparison,
  minDate,
}) => {
  const { filterGroups, setFilter, resetFilter, registerOnResetCallback } =
    useFilterContext();
  const filterKey = comparison
    ? FILTER_KEYS.DATE_RANGE_COMPARISON
    : FILTER_KEYS.DATE_RANGE;
  const dateRangeFilter = filterGroups[filterKey]?.[0];
  const dateRange = useMemo(
    () =>
      dateRangeFilter
        ? filterToDateRange(dateRangeFilter as FilterValue<DateRangeAsStrings>)
        : undefined,
    [dateRangeFilter],
  );
  const { t } = useTranslation('followUp');
  const anchorEl = useRef<HTMLDivElement>(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const onResetDateRange = useCallback(() => {
    resetFilter(filterKey);
    setModalIsOpen(false);
  }, [filterKey, resetFilter]);

  const onSubmitModal = (newDateRange: RequiredDateRange) => {
    setFilter(filterKey, [dateRangeToFilter(newDateRange, filterKey)]);
    setModalIsOpen(false);
  };

  useEffect(
    () => registerOnResetCallback(() => setModalIsOpen(false)),
    [registerOnResetCallback],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        justifyContent: 'space-between',
      }}
    >
      <TextField
        fullWidth
        value={dateRange ? dateRangeToReadableString(dateRange) : ''}
        placeholder={t('dateFilter.selectRange')}
        InputProps={{ readOnly: true }}
        inputProps={{ sx: { cursor: 'pointer' } }}
        role="button"
        onClick={() => setModalIsOpen(true)}
        ref={anchorEl}
        aria-label={
          comparison ? 'DateRangePickerComparison' : 'DateRangePicker'
        }
      />
      {!comparison && (
        <Button
          onClick={onResetDateRange}
          sx={{ flexShrink: 0 }}
          variant="cta"
          color="secondary"
          disabled={modalIsOpen}
        >
          {t('dateFilter.reset')}
        </Button>
      )}
      {modalIsOpen && (
        <DateFilterModal
          anchorEl={anchorEl.current}
          initialDateRange={
            dateRange ?? calculateInitialDateRange(undefined, minDate)
          }
          onClose={() => setModalIsOpen(false)}
          onSubmit={onSubmitModal}
          open
        />
      )}
    </Box>
  );
};

export default DateFilterControl;
